import React, { useMemo } from "react";

const WhatsAppShare = ({
    selectedFlights = [],
    fromCity,
    toCity,
    adults = 1,
    children = 0,
    infants = 0,
    cabinClass = "Economy",
    contact = "8447455883",
    email = "sibo.sarso@gmail.com",
    company = "Demo Company",
    buttonText = "Share on WhatsApp",
    className = "",
}) => {
    const formatDateTime = (dateTime, airportCode) => {
        if (!dateTime) return "";

        const date = new Date(dateTime);

        const datePart = date.toLocaleDateString("en-GB", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "2-digit",
        });

        const timePart = date.toLocaleTimeString("en-GB", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });

        return `${datePart} ${timePart} (${airportCode})`;
    };

    const getBaggageDetails = (fare) => {
        const cabinBaggage =
            fare?.Cabin ||
            fare?.FareBreak?.[0]?.CabinBaggage ||
            "";

        const checkInBaggage =
            fare?.Checkin ||
            fare?.FareBreak?.[0]?.Baggage ||
            "";

        return {
            cabinBaggage,
            checkInBaggage,
        };
    };

    const getStopsText = (flight) => {
        const stops = Number(flight?.Stops || 0);

        if (stops === 0) {
            return "Non-stop";
        }

        return `${stops} Stop${stops > 1 ? "s" : ""}`;
    };
    const getOnWardReturn = (isSegmentReturn) => {
        if (isSegmentReturn) {
            return "RETURN";
        }
        return `ONWARD`;
    };

    const buildMessage = () => {
        let message = `Hello, please find details with regards to your flights(s) query for:
*${fromCity} - ${toCity}*
${adults} Adult${adults > 1 ? "s" : ""}`;

        if (children > 0) {
            message += `
${children} Child${children > 1 ? "ren" : ""}`;
        }

        if (infants > 0) {
            message += `
${infants} Infant${infants > 1 ? "s" : ""}`;
        }

        message += `
${cabinClass}

Below mentioned prices are the total price(s) inclusive of taxes:
--------------------------------------------------------------------------------`;

        selectedFlights.forEach((flight) => {
            const selectedFare = flight?.FareList?.find(
                fare => fare.PriceID === flight.selectedPriceID
            );

            if (!selectedFare) {
                return;
            }

            const segment = flight?.Segments?.[0];

            if (!segment) {
                return;
            }

            const departure = formatDateTime(
                segment.originDepTime,
                segment.originAirportCode
            );

            const arrival = formatDateTime(
                segment.destinationArrTime,
                segment.destinationAirportCode
            );

            const duration =
                flight.Duration ||
                segment.FlightDuration ||
                "";

            const {
                cabinBaggage,
                checkInBaggage,
            } = getBaggageDetails(selectedFare);

            const stopText = getStopsText(flight);
            const OnWardReturn = getOnWardReturn(segment.isSegmentReturn);

            const price = Number(
                selectedFare.PublishedFare ??
                selectedFare.FarePerPax ??
                selectedFare.OfferedFare ??
                0
            );

            const formattedPrice = price.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });

            message += `
*${OnWardReturn}:* 🛫 *${flight.AirlineName}* (${flight.FlightNumber})
*Route: ${segment.originAirportCode}(${segment.originCityName})→${segment.destinationAirportCode}(${segment.destinationCityName})*
*${stopText}*
*Departure: ${departure}*
*Arrival: ${arrival}*
*Duration: ${duration}*
*ADULT: Baggage ${cabinBaggage} | Check-in ${checkInBaggage}*
*Price: ₹${formattedPrice}*
--------------------------------------------------------------------------------`;
        });

        message += `
Thank you for choosing *${company}*
In case of any support :
☎️ Contact : ${contact}
📧 Email : ${email}

Airline ticket pricing is dynamic. Fares are valid as of now and might change at the time of issuance.`;

        return message;
    };

    const message = useMemo(
        () => buildMessage(),
        [
            selectedFlights,
            fromCity,
            toCity,
            adults,
            children,
            infants,
            cabinClass,
            contact,
            email,
        ]
    );

    const handleWhatsAppShare = () => {
        if (!selectedFlights?.length) {
            return;
        }

        const encodedMessage = encodeURIComponent(message);

        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodedMessage}`;

        window.open(whatsappUrl, "_blank");
    };

    if (!selectedFlights?.length) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                onClick={handleWhatsAppShare}
                className={`
                inline-flex items-center justify-center
                gap-2 px-4 py-2
                rounded-lg
                bg-green-600
                text-white
                font-medium
                hover:bg-green-700
                transition
                ${className}
            `}
            >
                <svg className="h-5 w-5 fill-current" viewBox="0 0 24 24" xmlns="http://w3.org">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.454 5.709 1.455h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                {/* {buttonText} */}
            </button>
        </>

    );
};

export default WhatsAppShare;